import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { HttpParams, HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { debounceTime, distinctUntilChanged, switchMap, first, tap, take, map } from 'rxjs/operators';
import { Currencies, Event, EventPersonalization, EventStatus, EventWithStat, Gauge, Platforms, TicketingPlatform } from '../../models';

export interface GetMoreEventParam {
  limit: number,
  offset: number,
  order?: 'ASC' | 'DESC',
  searchQuery?: string,
  status?: EventStatus[],
  ticketingPlatform?: Platforms[],
  dateRangeStart?: string,
  dateRangeEnd?: string,
  creationDateRangeStart?: string,
  creationDateRangeEnd?: string,
  sort?: EventSortOptions,
  secondSort?: EventSortOptions,
  secondOrder?: 'ASC' | 'DESC',
  postalAddressId?: number,
}

type EventSortOptions = keyof Event | 'ticketNb' | 'waitList';

@Injectable({
  providedIn: 'root',
})
export class DashboardEventService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  currentEvent: Event;

  public allowEventEdit = new BehaviorSubject<boolean>(false);

  public getMoreParticipants = new Subject<{limit: number, offset: number, searchQuery: string}>();

  getAllEvents() {
    const route = `${this.configUrl}allEvents/`;
    const getMoreEvents = new Subject<GetMoreEventParam>();

    return {
      subject: getMoreEvents,
      observable: getMoreEvents.pipe(
        // wait 300ms after each keystroke before considering the term
        debounceTime(300),
        // ignore new term if same as previous term
        distinctUntilChanged(),
        // switch to new search observable each time the term changes
        switchMap( ({
          limit,
          offset,
          order,
          searchQuery,
          status,
          ticketingPlatform,
          dateRangeStart,
          dateRangeEnd,
          creationDateRangeStart,
          creationDateRangeEnd,
          sort,
          secondSort,
          secondOrder,
          postalAddressId,
        }) => {
          const params = new HttpParams({
            fromObject: {
              limit: limit.toString(),
              offset: offset.toString(),
              order: order ? order.toString() : '',
              searchQuery: searchQuery ? encodeURIComponent(searchQuery) : '',
              ...status?.filter( (s) => !!s)?.reduce( (acc, s, i) => ({[`status[${i}]`]: s, ...acc}), {}),
              ...ticketingPlatform?.filter( (tp) => !!tp)?.reduce( (acc, tp, i) => ({[`ticketingPlatform[${i}]`]: tp, ...acc}), {}),
              dateRangeStart: dateRangeStart ? dateRangeStart.toString() : '',
              dateRangeEnd: dateRangeEnd ? dateRangeEnd.toString() : '',
              creationDateRangeStart: creationDateRangeStart ? creationDateRangeStart.toString() : '',
              creationDateRangeEnd: creationDateRangeEnd ? creationDateRangeEnd.toString() : '',
              sort: sort ? sort.toString() : 'dateStart',
              secondSort: secondSort ? secondSort.toString() : undefined,
              secondOrder: secondOrder ? secondOrder.toString() : undefined,
              postalAddressId,
            },
          });
          return this.http.get<{rows: EventWithStat[], count: number}>(route, {params});
        }),
        map( (events) => ({
          count: events.count,
          rows: events.rows?.map( (e) => new EventWithStat(e)),
        })),
      ),
    };
  }

  getWaitlistData(eventId: number): Observable<{
    usersNb: Record<number, number>,
    ticketsNb: Record<number, number>,
    autopurchasePerCat: Record<number, number>,
    autopurchasePerCatAtMax: Record<number, number>,
    autopurchasePerCatPending: Record<number, number>,
    autopurchasePerCatTotal: Record<number, number>,
    gauges: Gauge,
  }>{
    const route = `${this.configUrl}e/o/${eventId}/waitlist/data`;
    return this.http.get<any>(route).pipe(
      first(),
    );
  }

  getAutoPurchasersData(eventId: number): Observable<{
    debitedTicketsSum: number,
    prepaidTicketsSum: number,
    debitedWithoutTicketCount: number,
    debitedAmountWithoutTicketSum: number,
    stillActiveAutoPurchase: number,
  }>{
    const route = `${this.configUrl}e/o/${eventId}/autoPurchasersData`;
    return this.http.get<any>(route).pipe(
      first(),
    );
  }

  getEventWithTicketingPlatforms(eventId: number): Observable<Event> {
    if (this.currentEvent && this.currentEvent.id === eventId) {
      return of(this.currentEvent);
    }
    const route = `${this.configUrl}eventBasics/${eventId}/ticketingPlatforms`;
    return this.http.get<Event>(route).pipe(
      first(),
    );
  }

  getEventById(eventId: number): Observable<Event> {
    if (this.currentEvent && this.currentEvent.id === eventId) {
      return of(this.currentEvent);
    }
    const route = `${this.configUrl}allEvents/${eventId}`;
    return this.http.get<Event>(route).pipe(
      tap((e) => {
        this.currentEvent = e;
        return e;
      }),
    );
  }

  getClosestEvent() {
    const route = `${this.configUrl}closestEvent`;
    return this.http.get<Event>(route).pipe(
      first(),
    );
  }

  getPendingPayouts(eventId: number) {
    const route = `${this.configUrl}e/a/${eventId}/pendingPayouts`;
    return this.http.get<Array<{
      pendingSellers: number;
      pendingPayouts: number;
      pendingAmount: number;
      currency: Currencies;
    }>>(route).pipe(
      first(),
    );
  }

  executePendingPayouts(eventId: number, categoryId?: number): Observable<any>{
    const route = `${this.configUrl}e/a/${eventId}/pendingPayouts`;
    return this.http.post<{
      errors: any,
      // mangopayApi.makePayOut already is an array of salerecords so we flatten
      // in this case only one saleRecord for each payout so each array has 1 element
      payoutsData: any[],
      payoutsMade: number,
      sucess: boolean,
    }>(route, {eventId, categoryId}).pipe(
      first(),
    );
  }

  updateEvent(event: Partial<Event> & {id: number}) {
    const route = `${this.configUrl}e/o/events/${event.id}`;
    return this.http.put(route, event).pipe(
      first(),
    );
  }

  /**
   * Create or updates TicketingPlatform in an array for an event
   * @param eventId number
   * @param ticketingPlatforms Array<TicketingPlatform>
   * @returns TicketingPlatform[]
   */
  updateEventTicketingPlatforms(eventId: number, ticketingPlatforms: Array<TicketingPlatform>) {
    const route = `${this.configUrl}e/a/${eventId}/ticketingPlatforms`;
    return this.http.put<Array<Partial<TicketingPlatform>>>(route, ticketingPlatforms).pipe(
      first(),
      map( (ticketingPlatforms) => ticketingPlatforms.map( (t) => new TicketingPlatform(t))),
    );
  }

  unsubscribeAllRegistrants(eventId: number): Observable<{count: number}> {
    const route = `${this.configUrl}e/a/${eventId}/unsubscribeAutopurchase`;
    return this.http.get<{count: number}>(route).pipe(
      first(),
    );
  }

  /** @Deprecated */
  getWelcomeData() {
    const route = `${this.configUrl}e/o/welcome`;
    return this.http.get<{
      expiredEvents: EventWithStat[],
      onlineEvents: EventWithStat[],
    }>(route).pipe(
      take(1),
      map( (events) => ({
        expiredEvents: events.expiredEvents.map( (e) => new EventWithStat(e)),
        onlineEvents: events.onlineEvents.map( (e) => new EventWithStat(e)),
      })),
    );
  }

  getAutomationScheduledDate(eventId: number) {
    const route = `${this.configUrl}e/o/${eventId}/automationScheduledDate`;
    return this.http.get<{time: string}>(route).pipe(
      first(),
    );
  }

  updateEventPersonalization(eventId: number, eventPersonalization: EventPersonalization) {
    const route = `${this.configUrl}e/o/${eventId}/eventPersonalization`;
    const cloneEventPerso = new EventPersonalization(eventPersonalization);
    Object.keys(cloneEventPerso).forEach( (key) => {
      if (typeof cloneEventPerso?.[key] === 'object' && !Array.isArray(cloneEventPerso?.[key]) && !cloneEventPerso.hasAtLeastOneTrad(key)) {
        cloneEventPerso[key] = null;
      }
    });
    return this.http.put(route, cloneEventPerso).pipe(
      first(),
      map( (p) => new EventPersonalization(p)),
    );
  }
}
